import React, { useState } from 'react';
import { Button, Card, Spin, message, Modal, Table, Pagination } from 'antd';
import { FileTextOutlined, RobotOutlined, CloseOutlined, HistoryOutlined, LoadingOutlined, PrinterOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';

// Academic medical report formatting function - similar to medical research papers
const formatMedicalReport = (content) => {
    if (!content) return content;

    // Handle line breaks
    let formattedContent = content.replace(/\\n/g, '\n').replace(/\n/g, '<br/>');

    // Academic table formatting - like research papers
    formattedContent = formattedContent.replace(
        /<table[^>]*>/g,
        '<div class="academic-table-wrapper my-6"><table class="academic-table w-full border-collapse bg-white shadow-lg">'
    );
    formattedContent = formattedContent.replace(/<\/table>/g, '</table></div>');

    // Medical section headers - academic style
    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*sectionHeader[^"]*"[^>]*)>/g,
        '<$1 class="academic-section bg-slate-600 text-white text-center py-3 px-4 font-bold text-base mb-0">'
    );

    // Medical content blocks - academic paper style
    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*content[^"]*"[^>]*)>/g,
        '<$1 class="academic-content bg-white p-6 text-gray-800 leading-relaxed border-l-4 border-slate-400 my-4">'
    );

    // Important medical information - academic highlight
    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*important[^"]*"[^>]*)>/g,
        '<$1 class="academic-highlight bg-slate-50 border border-slate-300 text-slate-800 px-4 py-2 my-2 inline-block font-medium">'
    );

    // Academic list styling
    formattedContent = formattedContent.replace(
        /<ul class='list'>/g,
        '<ul class="academic-list my-4 bg-white border border-gray-200">'
    );

    formattedContent = formattedContent.replace(
        /<ol class='list'>/g,
        '<ol class="academic-numbered-list my-4 bg-white border border-gray-200 counter-reset-[academic-counter]">'
    );

    formattedContent = formattedContent.replace(
        /<li class='listItem'>/g,
        '<li class="academic-list-item bg-gray-50 p-4 border-b border-gray-100 text-gray-700">'
    );

    // Academic text formatting
    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 font-bold">$1</strong>');
    formattedContent = formattedContent.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em class="text-gray-700 italic">$1</em>');
    formattedContent = formattedContent.replace(/__(.*?)__/g, '<u class="underline decoration-slate-500 decoration-2">$1</u>');

    // Academic code blocks
    formattedContent = formattedContent.replace(/```(.*?)```/gs, 
        '<div class="academic-code-block bg-gray-100 border border-gray-300 p-4 my-4 font-mono text-xl">' +
        '<pre class="text-gray-800 whitespace-pre-wrap leading-relaxed text-xl ">$1</pre></div>'
    );

    // Academic headers - research paper style
    formattedContent = formattedContent.replace(/^### (.*$)/gm, 
        '<h3 class="academic-h3 text-lg font-bold text-gray-900 mt-8 mb-4 pb-2 border-b-2 border-gray-300 uppercase tracking-wider">$1</h3>'
    );
    formattedContent = formattedContent.replace(/^## (.*$)/gm, 
        '<h2 class="academic-h2 text-xl font-bold text-gray-900 mt-10 mb-5 text-center pb-3 border-b-3 border-slate-500 uppercase tracking-wider">$1</h2>'
    );
    formattedContent = formattedContent.replace(/^# (.*$)/gm, 
        '<h1 class="academic-h1 text-2xl font-bold text-center text-gray-900 bg-gray-50 border-2 border-gray-300 p-6 mb-8 uppercase tracking-wider">$1</h1>'
    );

    // Academic list items
    formattedContent = formattedContent.replace(/^[\s]*[-*]\s(.*)$/gm, 
        '<li class="academic-bullet text-xl py-2 px-4 border-b border-gray-100 text-gray-700 leading-relaxed">• $1</li>'
    );

    formattedContent = formattedContent.replace(/^[\s]*\d+\.\s(.*)$/gm, 
        '<li class="academic-numbered py-2 px-4 border-b border-gray-100 text-gray-700 text-xl leading-relaxed counter-increment-[academic-counter]"><span class="font-bold text-slate-600 mr-2" data-counter></span>$1</li>'
    );

    // Professional academic links
    formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, 
        '<a href="$2" class="text-slate-600 hover:text-slate-800 font-medium underline" target="_blank">$1</a>'
    );

    return formattedContent;
};

const PatientSummaryComponent = ({ patient }) => {
    const [loading, setLoading] = useState(false);
    const [summaryVisible, setSummaryVisible] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [isHistoryView, setIsHistoryView] = useState(false);

    // History state
    const [historyVisible, setHistoryVisible] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPagination, setHistoryPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const generateSummary = async () => {
        if (!patient?.id) {
            message.error('Patient ID is required to generate summary');
            return;
        }

        setLoading(true);
        try {
            const response = await drEasyApi.post('/api/DrEasyAi/SummrizeReport', {
                userId: patient.id
            });

            if (response.data) {
                setSummaryData(response.data);
                setIsHistoryView(false);
                setSummaryVisible(true);
            } else {
                message.warning('No summary data available');
            }
        } catch (error) {
            console.error('Error generating summary:', error);
            message.error(error.response?.data?.[0] || 'Failed to generate summary');
        } finally {
            setLoading(false);
        }
    };

    const fetchSummaryHistory = async (pageNumber = 1, pageSize = 10) => {
        if (!patient?.id) {
            message.error('Patient ID is required to fetch history');
            return;
        }

        setHistoryLoading(true);
        try {
            const response = await drEasyApi.get('/api/DrEasyAi/GetSummrizeHistory', {
                params: {
                    UserId: patient.id,
                    PageNumber: pageNumber,
                    PageSize: pageSize
                }
            });

            if (response.data && response.data.items) {
                setHistoryData(response.data.items);
                setHistoryPagination({
                    current: response.data.pageNumber || pageNumber,
                    pageSize: response.data.pageSize || pageSize,
                    total: response.data.totalCount || 0
                });
                setHistoryVisible(true);
            } else {
                setHistoryData([]);
                message.info('No history data available');
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            setHistoryData([]);
            message.error(error.response?.data?.[0] || 'Failed to fetch summary history');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleViewHistory = () => {
        fetchSummaryHistory();
    };

    const handleHistoryPageChange = (page, pageSize) => {
        fetchSummaryHistory(page, pageSize);
    };

    const handleCloseSummary = () => {
        setSummaryVisible(false);
        setSummaryData(null);
        setIsHistoryView(false);
    };

    const handleCloseHistory = () => {
        setHistoryVisible(false);
        setHistoryData([]);
    };

    const viewHistoryItem = (historyItem) => {
        const summaryData = {
            summary: historyItem.summaryHtml,
            content: historyItem.summaryHtml,
            message: historyItem.summaryHtml,
            generatedAt: historyItem.createdAt,
            isHistorical: true
        };

        setSummaryData(summaryData);
        setIsHistoryView(true);
        setSummaryVisible(true);
        setHistoryVisible(false);
    };

    const printReport = () => {
        const printContent = document.getElementById('medical-report-content');
        if (printContent) {
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Medical Report - ${patient?.fullName}</title>
                    <style>
                        @media print {
                            * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
                        }
                        body {
                            font-family: 'Times New Roman', Georgia, serif;
                            line-height: 1.6;
                            color: #374151;
                            max-width: 210mm;
                            margin: 0 auto;
                            padding: 15mm;
                            background: white;
                        }
                        .academic-header {
                            text-align: center;
                            margin-bottom: 20px;
                            padding: 15px;
                            background: #475569;
                            color: white;
                            page-break-inside: avoid;
                        }
                        .academic-content { 
                            background: white;
                            border: 1px solid #d1d5db;
                            font-family: 'Times New Roman', Georgia, serif;
                            font-size: 20pt;
                            line-height: 1.5;
                        }
                        .patient-info-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                            border: 1px solid #374151;
                        }
                        .patient-info-table th {
                            background: #475569;
                            color: white;
                            padding: 8px 12px;
                            text-align: center;
                            font-weight: bold;
                            font-size: 11pt;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }
                        .patient-info-table td {
                            padding: 8px 12px;
                            border: 1px solid #d1d5db;
                            font-size: 10pt;
                        }
                        .patient-info-table tr:nth-child(even) td {
                            background: #f8fafc;
                        }
                        .academic-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 15px 0;
                            font-size: 10pt;
                            border: 1px solid #374151;
                        }
                        .academic-table th {
                            background: #475569 !important;
                            color: white !important;
                            padding: 8px 6px;
                            text-align: center;
                            font-weight: bold;
                            font-size: 9pt;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            border: 1px solid #374151;
                        }
                        .academic-table td {
                            padding: 6px 8px;
                            border: 1px solid #d1d5db;
                            text-align: center;
                            font-size: 9pt;
                        }
                        .academic-table tr:nth-child(even) {
                            background: #f8fafc !important;
                        }
                        .academic-section {
                            background: #475569 !important;
                            color: white !important;
                            padding: 8px 12px !important;
                            text-align: center;
                            font-weight: bold;
                            font-size: 11pt;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            margin: 15px 0 0 0 !important;
                        }
                        .academic-content {
                            padding: 12px !important;
                            background: white !important;
                            border-left: none !important;
                            font-size: 10pt;
                            line-height: 1.5;
                        }
                        h1, h2, h3 { 
                            color: #374151; 
                            page-break-after: avoid;
                            font-family: 'Times New Roman', Georgia, serif;
                        }
                        .academic-h1 { 
                            font-size: 14pt; 
                            font-weight: bold;
                            text-align: center;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                            background: #f8fafc !important;
                            border: 1px solid #d1d5db !important;
                            color: #374151 !important;
                            padding: 12px !important;
                        }
                        .academic-h2 { 
                            font-size: 12pt; 
                            font-weight: bold;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            border-bottom: 2px solid #475569 !important;
                            text-align: center;
                        }
                        .academic-h3 { 
                            font-size: 11pt; 
                            font-weight: bold;
                            border-bottom: 1px solid #d1d5db !important;
                        }
                        .academic-list {
                            background: white !important;
                            border: 1px solid #d1d5db !important;
                            margin: 10px 0;
                        }
                        .academic-list-item {
                            background: #f8fafc !important;
                            padding: 6px 12px !important;
                            border-bottom: 1px solid #e5e7eb !important;
                            font-size: 10pt;
                        }
                        .academic-highlight {
                            background: #f1f5f9 !important;
                            border: 1px solid #cbd5e1 !important;
                            color: #374151 !important;
                            padding: 4px 8px !important;
                            font-size: 9pt;
                            font-weight: bold;
                        }
                        @page { 
                            margin: 15mm; 
                            size: A4;
                        }
                        .page-break { page-break-before: always; }
                    </style>
                </head>
                <body>
                    <div class="academic-header">
                        <h1 style="margin: 0; font-size: 18pt; text-transform: uppercase; letter-spacing: 1px;">Medical Analysis Report</h1>
                        <p style="margin: 8px 0 0 0; font-size: 11pt;">Clinical Data Analysis & Assessment</p>
                    </div>
                    
                    <table class="patient-info-table">
                        <thead>
                            <tr>
                                <th colspan="4">Patient Information</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style="background: #f8fafc;"><strong>Patient Name:</strong></td>
                                <td>${patient?.fullName || 'N/A'}</td>
                                <td style="background: #f8fafc;"><strong>Patient ID:</strong></td>
                                <td>${patient?.id || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td style="background: #f8fafc;"><strong>Report Date:</strong></td>
                                <td>${isHistoryView && summaryData?.generatedAt 
                                    ? new Date(summaryData.generatedAt).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })
                                    : new Date().toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })
                                }</td>
                                <td style="background: #f8fafc;"><strong>Analysis Time:</strong></td>
                                <td>${isHistoryView && summaryData?.generatedAt 
                                    ? new Date(summaryData.generatedAt).toLocaleTimeString('en-US', { 
                                        hour: '2-digit', 
                                        minute: '2-digit', 
                                        hour12: true 
                                    })
                                    : new Date().toLocaleTimeString('en-US', { 
                                        hour: '2-digit', 
                                        minute: '2-digit', 
                                        hour12: true 
                                    })
                                }</td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <div class="academic-content">
                        ${printContent.innerHTML}
                    </div>
                    
                    <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 9pt;">
                        <div style="margin-bottom: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px;">
                            Medical Disclaimer
                        </div>
                        <p style="margin: 8px 0; line-height: 1.4;">
                            This report is generated by artificial intelligence for informational and analytical purposes only. 
                            All medical decisions should be made in consultation with qualified healthcare professionals. 
                            This analysis does not substitute professional medical advice, diagnosis, or treatment.
                        </p>
                        <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #d1d5db; font-size: 8pt;">
                            Generated by DrEasy AI Medical Intelligence System | 
                            Report ID: DR-${patient?.id}-${isHistoryView && summaryData?.generatedAt 
                                ? new Date(summaryData.generatedAt).toISOString().slice(0,10)
                                : new Date().toISOString().slice(0,10)
                            }
                        </div>
                    </div>
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    // Mobile-responsive table columns
    const historyColumns = [
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => (
                <div className="text-xs sm:text-sm">
                    <div className="font-medium text-gray-900">{new Date(date).toLocaleDateString()}</div>
                    <div className="text-gray-500">{new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
            ),
            width: '25%',
        },
        {
            title: 'Report Preview',
            dataIndex: 'summaryHtml',
            key: 'summaryHtml',
            render: (summaryHtml) => {
                if (!summaryHtml) return <span className="text-gray-400 italic">No preview available</span>;

                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = summaryHtml;
                const plainText = tempDiv.textContent || tempDiv.innerText || '';

                return (
                    <div className="min-w-0">
                        <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
                            {plainText.substring(0, 120) + (plainText.length > 120 ? '...' : '')}
                        </p>
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => viewHistoryItem(record)}
                    className="text-slate-600 hover:text-slate-800 p-0 font-medium"
                    icon={<FileTextOutlined />}
                >
                    View Report
                </Button>
            ),
            width: '20%',
        },
    ];

    return (
        <>
            {/* Generate Report Button */}
            <div className="w-full">
                <button
                    type=""
                    onClick={generateSummary}
                    className="h-8 sm:h-10  font-semibold text-lg border px-2 bg-primary border-primary border-1 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 w-full block"
                >
                    {loading ? (
                        <span><LoadingOutlined className='animate-spin text-white'/></span>
                    ) : (
                        <span className="star-magic text-secondary">✨</span>
                    )}
                </button>
            </div>


            {/* Enhanced Academic Medical Report Modal - Now consistent for both new and historical reports */}
            <Modal
                title={
                    <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
                        {/* <img
                            src='https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg'
                            width={48}
                            height={48}
                            className='rounded-xl object-cover border-2 border-slate-200'
                            alt="DrEasy Logo"
                        /> */}
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Medical Analysis Report {isHistoryView && <span className="text-lg text-slate-600">(Historical)</span>}
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Patient:</span>
                                    <span className="font-semibold text-gray-900">{patient?.fullName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">ID:</span>
                                    <span className="font-semibold text-gray-900">{patient?.id}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Generated:</span>
                                    <span className="font-medium text-gray-700">
                                        {isHistoryView && summaryData?.generatedAt 
                                            ? new Date(summaryData.generatedAt).toLocaleString()
                                            : new Date().toLocaleString()
                                        }
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Status:</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        isHistoryView 
                                            ? 'bg-blue-100 text-blue-800' 
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {isHistoryView ? 'Historical Report' : 'AI Analyzed'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                open={summaryVisible}
                onCancel={handleCloseSummary}
                footer={[
                    <div key="footer" className="flex justify-between items-center w-full pt-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                            <MedicineBoxOutlined />
                            Powered by DrEasy AI Medical Intelligence
                        </div>
                        <div className="flex gap-3">
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={printReport}
                                className="border-slate-500 text-slate-600 hover:bg-slate-50"
                            >
                                Print Report
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleCloseSummary}
                                className="bg-slate-600 border-slate-600"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                ]}
                width="95%"
                style={{ maxWidth: '900px', top: 20 }}
                bodyStyle={{
                    maxHeight: '75vh',
                    overflowY: 'auto',
                    padding: 0,
                    background: '#f8fafc'
                }}
                className="medical-report-modal"
            >
                {summaryData ? (
                    <div className="p-6">
                        {/* Academic Medical Report Header */}
                        <div className="bg-slate-600 text-white text-center py-4 mb-0 shadow-sm">
                            <h1 className="text-xl font-bold mb-1 uppercase tracking-wider">Medical Analysis Report</h1>
                            <p className="text-slate-200 text-sm">Clinical Data Analysis & Assessment</p>
                        </div>

                        {/* Patient Information Table - Academic Style */}
                        <div className="bg-white border border-gray-300 mb-6">
                            <div className="bg-slate-600 text-white text-center py-2 px-4 font-bold text-sm uppercase tracking-wider">
                                Patient Information
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-gray-300">
                                <div className="p-3 border-r border-gray-300 bg-gray-50">
                                    <span className="font-semibold text-gray-700">Patient Name:</span>
                                    <span className="ml-2 text-gray-900">{patient?.fullName || 'N/A'}</span>
                                </div>
                                <div className="p-3 bg-white">
                                    <span className="font-semibold text-gray-700">Patient ID:</span>
                                    <span className="ml-2 text-gray-900">{patient?.id || 'N/A'}</span>
                                </div>
                                <div className="p-3 border-r border-gray-300 border-t border-gray-300 bg-gray-50">
                                    <span className="font-semibold text-gray-700">Report Date:</span>
                                    <span className="ml-2 text-gray-900">{isHistoryView && summaryData?.generatedAt 
                                        ? new Date(summaryData.generatedAt).toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                        })
                                        : new Date().toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                        })
                                    }</span>
                                </div>
                                <div className="p-3 border-t border-gray-300 bg-white">
                                    <span className="font-semibold text-gray-700">Analysis Time:</span>
                                    <span className="ml-2 text-gray-900">{isHistoryView && summaryData?.generatedAt 
                                        ? new Date(summaryData.generatedAt).toLocaleTimeString('en-US', { 
                                            hour: '2-digit', 
                                            minute: '2-digit', 
                                            hour12: true 
                                        })
                                        : new Date().toLocaleTimeString('en-US', { 
                                            hour: '2-digit', 
                                            minute: '2-digit', 
                                            hour12: true 
                                        })
                                    }</span>
                                </div>
                            </div>
                        </div>

                        {/* Report Content - Academic Style - Now consistent for both new and historical reports */}
                        <div
                            id="medical-report-content"
                            className="bg-white border border-gray-300 shadow-sm academic-report-content p-2"
                            style={{
                                direction: 'rtl',
                                textAlign: 'right',
                                fontFamily: 'Times New Roman, Georgia, serif',
                                fontSize: '18px',
                                lineHeight: '1.6',
                                color: '#1F6F7D'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: formatMedicalReport(summaryData.summary || summaryData.content || summaryData.message || JSON.stringify(summaryData))
                            }}
                        />

                        {/* Academic Footer */}
                        <div className="mt-6 bg-gray-50 border border-gray-300 p-4 text-center">
                         
                            <div className="mt-3 pt-2 border-t border-gray-300 text-xs text-gray-500">
                                Generated by DrEasy AI Medical Intelligence System | 
                                Report ID: DR-{patient?.id}-{isHistoryView && summaryData?.generatedAt 
                                    ? new Date(summaryData.generatedAt).toISOString().slice(0,10)
                                    : new Date().toISOString().slice(0,10)
                                }
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-16 px-6">
                        <div className="mb-6">
                            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center mb-4 animate-pulse">
                                <MedicineBoxOutlined className="text-3xl text-white" />
                            </div>
                            <Spin size="large" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            🔍 Analyzing Medical Data
                        </h3>
                        <p className="text-gray-600 mb-4 max-w-md mx-auto">
                            Our AI medical intelligence system is carefully reviewing and analyzing the patient's medical information to generate a comprehensive academic report.
                        </p>
                        <div className="text-sm text-gray-500">
                            This process may take a few moments to ensure accurate analysis
                        </div>
                    </div>
                )}
            </Modal>

            {/* Enhanced CSS Styles - Academic Medical Report */}
            <style jsx global>{`
                /* Academic medical report styling - Research paper style */
                .academic-report-content {
                    font-family: 'Times New Roman', Georgia, serif !important;
                    font-size: 14px !important;
                    line-height: 1.6 !important;
                    color: #374151 !important;
                }

                .academic-table-wrapper {
                    margin: 20px 0;
                    overflow-x: auto;
                }

                .academic-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                    border: 2px solid #374151 !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                    font-size: 12px !important;
                    margin: 0 !important;
                }

                .academic-table th {
                    background: #475569 !important;
                    color: white !important;
                    padding: 8px 12px !important;
                    text-align: center !important;
                    font-weight: bold !important;
                    border: 1px solid #374151 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;
                }

                .academic-table td {
                    padding: 8px 12px !important;
                    border: 1px solid #d1d5db !important;
                    text-align: center !important;
                    font-size: 11px !important;
                    background: white !important;
                }

                .academic-table tr:nth-child(even) td {
                    background: #f8fafc !important;
                }

                .academic-table tr:nth-child(odd) td {
                    background: white !important;
                }

                /* Academic section headers - like research tables */
                .academic-section {
                    background: #475569 !important;
                    color: white !important;
                    padding: 8px 16px !important;
                    text-align: center !important;
                    font-weight: bold !important;
                    font-size: 13px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;
                    margin: 16px 0 0 0 !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                }

                .academic-content {
                    background: white !important;
                    padding: 16px !important;
                    border: none !important;
                    border-left: none !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                    font-size: 13px !important;
                    line-height: 1.6 !important;
                    text-align: justify !important;
                }

                .academic-highlight {
                    background: #f1f5f9 !important;
                    border: 1px solid #cbd5e1 !important;
                    color: #374151 !important;
                    padding: 6px 12px !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                    display: inline-block !important;
                    margin: 4px 8px 4px 0 !important;
                }

                /* Academic headers - research paper style */
                .academic-h1 {
                    font-size: 16px !important;
                    font-weight: bold !important;
                    text-align: center !important;
                    text-transform: uppercase !important;
                    letter-spacing: 1px !important;
                    background: #f8fafc !important;
                    border: 2px solid #d1d5db !important;
                    color: #374151 !important;
                    padding: 12px !important;
                    margin: 20px 0 !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                }

                .academic-h2 {
                    font-size: 14px !important;
                    font-weight: bold !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;
                    border-bottom: 2px solid #475569 !important;
                    text-align: center !important;
                    padding-bottom: 8px !important;
                    margin: 20px 0 12px 0 !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                }

                .academic-h3 {
                    font-size: 13px !important;
                    font-weight: bold !important;
                    border-bottom: 1px solid #d1d5db !important;
                    padding-bottom: 4px !important;
                    margin: 16px 0 8px 0 !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                }

                /* Academic lists */
                .academic-list, .academic-numbered-list {
                    background: white !important;
                    border: 1px solid #d1d5db !important;
                    margin: 12px 0 !important;
                    padding: 0 !important;
                }

                .academic-list-item, .academic-bullet, .academic-numbered {
                    background: #f8fafc !important;
                    padding: 8px 16px !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    font-size: 12px !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                    line-height: 1.5 !important;
                }

                .academic-list-item:last-child,
                .academic-bullet:last-child,
                .academic-numbered:last-child {
                    border-bottom: none !important;
                }

                .academic-list-item:nth-child(even),
                .academic-bullet:nth-child(even),
                .academic-numbered:nth-child(even) {
                    background: white !important;
                }

                /* Academic code blocks */
                .academic-code-block {
                    background: #f8fafc !important;
                    border: 1px solid #d1d5db !important;
                    padding: 12px !important;
                    margin: 12px 0 !important;
                    font-family: 'Courier New', monospace !important;
                    font-size: 11px !important;
                }

                .academic-code-block pre {
                    color: #374151 !important;
                    white-space: pre-wrap !important;
                    line-height: 1.4 !important;
                    margin: 0 !important;
                }

                /* Academic numbered list counters */
                .academic-numbered-list {
                    counter-reset: academic-counter;
                }

                .academic-numbered {
                    counter-increment: academic-counter;
                    position: relative;
                    padding-left: 40px !important;
                }

                .academic-numbered::before {
                    content: counter(academic-counter) ".";
                    position: absolute;
                    left: 16px;
                    font-weight: bold;
                    color: #475569;
                }

                /* Statistical formatting - like research papers */
                .statistical-value {
                    font-family: 'Courier New', monospace !important;
                    font-weight: bold !important;
                    color: #374151 !important;
                }

                .p-value {
                    font-style: italic !important;
                    font-family: 'Times New Roman', Georgia, serif !important;
                }

                /* Academic paragraph styling */
                .academic-content p {
                    text-align: justify !important;
                    text-indent: 0 !important;
                    margin-bottom: 8px !important;
                    line-height: 1.6 !important;
                }

                /* Research paper style blockquotes */
                .academic-content blockquote {
                    border-left: 3px solid #475569 !important;
                    padding-left: 16px !important;
                    margin: 16px 0 !important;
                    font-style: italic !important;
                    background: #f8fafc !important;
                    padding: 12px 16px !important;
                    border-radius: 0 !important;
                }

                /* Academic inline elements */
                .academic-content strong {
                    font-weight: bold !important;
                    color: #1f2937 !important;
                }

                .academic-content em {
                    font-style: italic !important;
                    color: #4b5563 !important;
                }

                /* Academic links */
                .academic-content a {
                    color: #475569 !important;
                    text-decoration: underline !important;
                    font-weight: normal !important;
                }

                .academic-content a:hover {
                    color: #1f2937 !important;
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .medical-report-modal .ant-modal {
                        margin: 8px !important;
                        max-width: calc(100vw - 16px) !important;
                    }
                    
                    .medical-report-modal .ant-modal-content {
                        margin: 0;
                    }
                    
                    .medical-report-modal .ant-modal-header {
                        padding: 16px !important;
                    }
                    
                    .medical-report-modal .ant-modal-body {
                        padding: 0 !important;
                    }
                    
                    .medical-report-modal .ant-modal-footer {
                        padding: 16px !important;
                    }

                    .academic-h1 {
                        font-size: 14px !important;
                        padding: 8px !important;
                    }

                    .academic-h2 {
                        font-size: 13px !important;
                    }

                    .academic-h3 {
                        font-size: 12px !important;
                    }

                    .academic-content {
                        font-size: 12px !important;
                        line-height: 1.5 !important;
                        padding: 12px !important;
                    }

                    .academic-list, .academic-numbered-list {
                        margin: 8px 0 !important;
                    }

                    .academic-list-item, .academic-bullet, .academic-numbered {
                        padding: 6px 12px !important;
                        font-size: 11px !important;
                    }

                    .academic-table {
                        font-size: 10px !important;
                    }

                    .academic-table th, .academic-table td {
                        padding: 4px 6px !important;
                        font-size: 9px !important;
                    }
                }

                @media (max-width: 480px) {
                    .medical-report-modal .ant-modal {
                        margin: 4px !important;
                        max-width: calc(100vw - 8px) !important;
                    }

                    .academic-content {
                        font-size: 11px !important;
                        padding: 8px !important;
                    }

                    .academic-h1 {
                        font-size: 12px !important;
                        padding: 6px !important;
                    }

                    .academic-section {
                        font-size: 10px !important;
                        padding: 6px 12px !important;
                    }

                    .academic-table {
                        font-size: 9px !important;
                    }

                    .academic-table th, .academic-table td {
                        padding: 3px 4px !important;
                        font-size: 8px !important;
                    }
                }

                /* Print specific styles for academic format */
                @media print {
                    .academic-report-content {
                        font-size: 10pt !important;
                        line-height: 1.5 !important;
                    }

                    .academic-table {
                        font-size: 8pt !important;
                    }

                    .academic-table th {
                        font-size: 8pt !important;
                        background: #475569 !important;
                        color: white !important;
                    }

                    .academic-table td {
                        font-size: 8pt !important;
                    }

                    .academic-section {
                        background: #475569 !important;
                        color: white !important;
                        font-size: 9pt !important;
                    }

                    .academic-h1 {
                        font-size: 12pt !important;
                        page-break-after: avoid !important;
                    }

                    .academic-h2 {
                        font-size: 11pt !important;
                        page-break-after: avoid !important;
                    }

                    .academic-h3 {
                        font-size: 10pt !important;
                        page-break-after: avoid !important;
                    }

                    .academic-list-item,
                    .academic-bullet,
                    .academic-numbered {
                        font-size: 9pt !important;
                        page-break-inside: avoid !important;
                    }

                    .academic-highlight {
                        background: #f1f5f9 !important;
                        border: 1px solid #cbd5e1 !important;
                        font-size: 8pt !important;
                    }

                    .academic-table-wrapper {
                        page-break-inside: avoid !important;
                    }

                    /* Hide modal chrome during print */
                    .ant-modal-mask,
                    .ant-modal-wrap,
                    .ant-modal-header,
                    .ant-modal-footer,
                    .ant-modal-close {
                        display: none !important;
                    }
                }

                /* Enhanced accessibility and interactions */
                .academic-bullet:hover,
                .academic-numbered:hover,
                .academic-list-item:hover {
                    background-color: #f1f5f9 !important;
                }

                .academic-highlight {
                    transition: all 0.2s ease !important;
                }

                .academic-highlight:hover {
                    background-color: #e2e8f0 !important;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                /* Custom scrollbar for modal content */
                .medical-report-modal .ant-modal-body::-webkit-scrollbar {
                    width: 8px;
                }
                
                .medical-report-modal .ant-modal-body::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }
                
                .medical-report-modal .ant-modal-body::-webkit-scrollbar-thumb {
                    background: #475569;
                    border-radius: 4px;
                }
                
                .medical-report-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
                    background: #334155;
                }

                /* Loading state improvements */
                .ant-spin-container {
                    transition: opacity 0.3s ease;
                }

                .ant-btn-loading {
                    pointer-events: none;
                    opacity: 0.7;
                }

                /* Table enhancements */
                .ant-table-thead > tr > th {
                    background-color: #f8fafc !important;
                    border-bottom: 2px solid #e5e7eb !important;
                    font-weight: 600 !important;
                    color: #374151 !important;
                }

                .ant-table-tbody > tr {
                    transition: all 0.2s ease;
                }

                .ant-table-tbody > tr:hover {
                    background-color: #f8fafc !important;
                }

                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f3f4f6 !important;
                }

                /* Enhanced focus states */
                .ant-btn:focus,
                .ant-btn:focus-visible {
                    outline: 2px solid #475569;
                    outline-offset: 2px;
                }

                /* Medical report specific animations */
                .academic-content {
                    animation: fadeInUp 0.6s ease-out;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Line clamp utilities */
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .line-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </>
    );
};

export default PatientSummaryComponent;